import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Search, Ticket, User, Plus, Star, Clock, MapPin } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const STATUS_LABELS = {
  pendiente_pago: "Pendiente",
  confirmado: "Confirmado",
  completado: "Completado",
  cancelado: "Cancelado",
};
const tabs = ["Todos", "pendiente_pago", "confirmado", "completado", "cancelado"];

const ClientHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Todos");
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewedIds, setReviewedIds] = useState(new Set());
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.listReservations();
      setReservations(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = tab === "Todos" ? reservations : reservations.filter((r) => r.status === tab);

  const cancel = async (id) => {
    setError("");
    try {
      await clientApi.cancelReservation(id, "Cancelado por el cliente");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cancelar la reserva.");
    }
  };

  const submitReview = async (id) => {
    setError("");
    try {
      await clientApi.createReview(id, { rating: Number(reviewForm.rating), comment: reviewForm.comment || null });
      setReviewedIds((prev) => new Set(prev).add(id));
      setReviewingId(null);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar la reseña.");
    }
  };

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-history">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Reservas</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Todos tus viajes</h1>

        {error && <p className="mt-4 text-xs text-red-400 font-body">{error}</p>}

        <div className="mt-8 flex items-center gap-2 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 border rounded-full text-[10px] uppercase tracking-wider font-display transition ${
                tab === t ? "bg-[#C9A227] border-[#C9A227] text-[#0D0E10] font-bold" : "border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227]/50"
              }`}
              data-testid={`filter-tab-${t}`}
            >
              {t === "Todos" ? t : STATUS_LABELS[t]}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {loading && <p className="text-xs text-[#A0A0A5] font-body">Cargando…</p>}
          {!loading && filtered.length === 0 && <p className="text-xs text-[#A0A0A5] font-body">No tienes reservas en este estado.</p>}

          {filtered.map((r) => (
            <div key={r.id} className="bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/40 rounded-xl p-5 lg:p-6" data-testid={`history-item-${r.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-[10px] font-mono text-[#A0A0A5]">{r.id.slice(0, 8).toUpperCase()}</span>
                    <StatusBadge status={STATUS_LABELS[r.status]} />
                    <span className="text-[10px] text-[#A0A0A5] uppercase tracking-wider">{r.modality}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-5 text-xs text-[#A0A0A5] flex-wrap">
                    <span className="flex items-center gap-1.5">
                      <Clock size={12} /> {new Date(r.scheduled_at).toLocaleString("es-PE")}
                    </span>
                    {r.passenger_point_address && (
                      <span className="flex items-center gap-1.5">
                        <MapPin size={12} /> {r.passenger_point_address}
                      </span>
                    )}
                    {r.seat_numbers.length > 0 && <span>Asientos: {r.seat_numbers.join(", ")}</span>}
                  </div>
                </div>
                <div className="flex md:flex-col items-center md:items-end justify-between gap-3">
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5]">Total</p>
                    <p className="font-display font-bold text-lg text-[#C9A227]">S/ {r.price_total}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/cliente/ticket?reservationId=${r.id}`)}
                    className="px-4 py-2 border border-[#2E2F33] hover:border-[#C9A227] text-[#F5F5F5] hover:text-[#C9A227] uppercase tracking-wider font-display text-[10px] rounded-lg"
                    data-testid={`view-detail-${r.id}`}
                  >
                    Ver detalle
                  </button>
                  {(r.status === "pendiente_pago" || r.status === "confirmado") && (
                    <button
                      onClick={() => cancel(r.id)}
                      className="px-4 py-2 border border-red-500/30 text-red-300 hover:bg-red-500/10 uppercase tracking-wider font-display text-[10px] rounded-lg"
                      data-testid={`cancel-${r.id}`}
                    >
                      Cancelar
                    </button>
                  )}
                  {r.status === "completado" && !reviewedIds.has(r.id) && (
                    <button
                      onClick={() => setReviewingId(reviewingId === r.id ? null : r.id)}
                      className="px-4 py-2 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display text-[10px] rounded-lg flex items-center gap-1"
                      data-testid={`review-${r.id}`}
                    >
                      <Star size={11} /> Reseñar
                    </button>
                  )}
                  {reviewedIds.has(r.id) && <span className="text-[10px] text-[#C9A227] uppercase tracking-wider font-display">Reseñado</span>}
                </div>
              </div>

              {reviewingId === r.id && (
                <div className="mt-4 pt-4 border-t border-[#2E2F33]" data-testid={`review-form-${r.id}`}>
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: n }))} data-testid={`star-${n}`}>
                        <Star size={20} className={n <= reviewForm.rating ? "text-[#C9A227]" : "text-[#2E2F33]"} fill={n <= reviewForm.rating ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    rows={2}
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                    placeholder="Cuéntanos cómo fue tu viaje (opcional)"
                    className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
                    data-testid={`review-comment-${r.id}`}
                  />
                  <button
                    onClick={() => submitReview(r.id)}
                    className="mt-3 px-5 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                    data-testid={`submit-review-${r.id}`}
                  >
                    Enviar reseña
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </PanelShell>
  );
};

export default ClientHistory;
